import $ from 'jquery';
import _ from 'lodash';
import Promise from 'bluebird';
import Backbone from 'backbone';
import BSModuleBB from 'model/BSModuleBB';
import JSON from 'json3';
import {Perm, TypePerm} from "module/edesc/TypePerm";
import Paramdiv from "collection/ParamdivCollection";
import {fetchAppDeviceStrings} from 'module/edesc/EntryDescUtil';
import {SisName} from 'util/SisName';
import {generateErrorHandler} from 'module/ConfirmDialog/compat/JQueryErrorHandlers';
import {alertError} from 'module/ConfirmDialog/component/ConfirmDialogContainer';
import {getCustomFieldsFrom} from 'module/CustomField/util/getCustomFieldsFrom';


let $_defer = $.Deferred();
let $_deferInitIds = $.Deferred();
let initEntryDescriptionMaps;

//constant definitions
const initFromSource = {
	"0001": "normalStart",
	"0002": "coreStart",
	"0003": "flexStart",

	"1001": "normalEnd",
	"1002": "coreEnd",
	"1003": "flexEnd",

	"2001": "startStepSize",
	"2002": "endStepSize",
};
const reqMapping = {
	"buchzeitVonZwingend": "bookingTime",
	"buchzspZwingend": "bookingTimespan",
	"einstufZwingend": "einstuf",
	"butextZwingend": "text",
};
const visMapping = {
	"buchzeitVonEingebbar": "bookingTime",
	"buchzeitBisEingebbar": "bookingTimeEnd",
	"buchzspEingebbar": "bookingTimespan",
	"einstufEingebbar": "einstuf",
};

/**
 * @typedef {Object} EntryDesc
 * @property {String} bookingType
 * @property {String} bookingTypeText
 * @property {boolean} buchzeitBisEingebbar
 * @property {Integer} buchzeitBisInit
 * @property {String} buchzeitBisInitAus
 * @property {Integer} buchzeitBisTakt
 * @property {String} buchzeitBisTaktA
 * @property {String} buchzeitBisTaktAus
 * @property {boolean} buchzeitVonEingebbar
 * @property {Integer} buchzeitVonInit
 * @property {String} buchzeitVonInitAus
 * @property {Integer} buchzeitVonTakt
 * @property {String} buchzeitVonTaktAus
 * @property {boolean} buchzeitVonZwingend
 * @property {boolean} buchzspEingebbar
 * @property {Integer} buchzspInit
 * @property {boolean} buchzspZwingend
 * @property {String} butextDq
 * @property {String} butextKostmm
 * @property {Integer} butextLaenge
 * @property {String} butextText
 * @property {String} butextTyp
 * @property {boolean} butextZwingend
 * @property {boolean} checkMaxDuration
 * @property {boolean} checkbox
 * @property {ZspArt} zspArt
 * @property {Array<CustomFieldTO>} customFieldDefinitions
 * @property {boolean} einstufEingebbar
 * @property {Integer} einstufNK
 * @property {String} einstufText
 * @property {boolean} einstufZwingend
 * @property {String} hourCategory
 * @property {String} hourCategoryText
 * @property {Integer} id
 * @property {TypePerm} perm
 * @property {Integer} sortKey
 * @property {TypeColor} terminalColor
 * @property {TypeColor} typeCategoryColor
 * @property {TypeColor} typeColor
 *
 * locally created:
 * @property {String} sisName
 * @property {String} label
 * @property {String} primaryShortLabel stuart or buart
 * @property {String} secondaryShortLabel buart or ''
 * @property {String} shortLabel YY or XX(YY)
 * @property {String} categoryLabel
 * @property {String} longLabel full info
 * @property {String} majorType
 */
let EntryDescriptionModel = Backbone.Model.extend({
	getSisName: function () {
		let bt = this.get('bookingType');
		if (!bt || !bt.trim()) return SisName.Persurl;
		else return SisName.Booking;
	},
	parse(attr) {
		let {bookingType, hourCategory} = attr;
		bookingType = bookingType.trim();
		hourCategory = hourCategory.trim();
		if (bookingType) {
			attr.sisName = SisName.Booking;
			attr.majorType = bookingType;
			attr.primaryShortLabel = hourCategory || bookingType;
			attr.secondaryShortLabel = hourCategory && bookingType || '';
		} else {
			attr.sisName = SisName.Persurl;
			attr.majorType = hourCategory;
			attr.primaryShortLabel = hourCategory;
			attr.secondaryShortLabel = '';
		}
		let shortLabel = hourCategory ? attr.hourCategory : attr.bookingType;
		if (attr.secondaryShortLabel) shortLabel += `(${attr.secondaryShortLabel})`;
		attr.shortLabel = shortLabel;
		const text = attr.text = hourCategory ? attr.hourCategoryText : attr.bookingTypeText;
		attr.label = shortLabel + ' - ' + text;
		if (!bookingType) {
			attr.categoryLabel = 'Ganztägig';
		} else if (!hourCategory) {
			attr.categoryLabel = 'Buchung';
		} else {
			attr.categoryLabel = attr.bookingTypeText;
		}
		attr.longLabel = `${shortLabel} ${text} (${attr.categoryLabel})`;
		attr.longLabelOnly = `${text} (${attr.categoryLabel})`;
		if (_.startsWith(attr.dstField, 'cf.') && attr.startIndex !== 0) {
			attr.startIndex = 0;
			log.error(`Wrong parameter in bookingType=${attr.bookingType} hourCategory=${attr.hourCategory} -> replacing startindex with 0`);
			void alertError("Fehlerhafte Parametrierung des Startindex");
		}
		return attr;
	},
	getCustomFieldDefinitions(selfAdmin) {
		return getCustomFieldsFrom(this.attributes, selfAdmin)
	},
	getCustomField(id, selfAdmin) {
		id = parseInt(id);
		if (isNaN(id)) return;
		return _.find(this.getCustomFieldDefinitions(selfAdmin), cfDef => cfDef.id === id);
	},
	getMeta(boundaries) {
		if (!boundaries) throw "Person boundaries not defined.";

		let checkMaxDuration = !!this.get('checkMaxDuration');
		let meta = {
			required: {
				bookingTime: false,
				bookingTimeEnd: false,
				bookingTimespan: false,
				einstuf: false,
				text: false,
			},
			visible: {
				bookingTime: false,
				bookingTimeEnd: false,
				bookingTimespan: false,
				einstuf: false,
				text: false,
			},
			textType: this.get("butextTyp"),
			textLength: this.get("butextLaenge"),
			checkMaxDuration: checkMaxDuration,
			bookingTime: undefined,
			bookingTimeEnd: undefined,
			bookingTimespan: undefined,
			startStepSize: undefined,
			endStepSize: undefined,
			endExclusion: undefined,
			maxDuration: undefined,
		};

		_.each(_.keysIn(reqMapping), (key) => {
			meta.required[reqMapping[key]] = this.get(key);
		});
		_.each(_.keysIn(visMapping), (key) => {
			meta.visible[visMapping[key]] = this.get(key);
		});
		meta.visible.text = this.get('butextLaenge') > 0;
		meta.bookingTimespan = this.get('buchzspInit');
		meta.zspArt = this.get('zspArt');

		meta.bookingTime = boundaries[initFromSource[this.get("buchzeitVonInitAus")]];
		meta.bookingTimeEnd = boundaries[initFromSource[this.get("buchzeitBisInitAus")]];
		meta.startStepSize = boundaries[initFromSource[this.get("buchzeitVonTaktAus")]];
		meta.endStepSize = boundaries[initFromSource[this.get("buchzeitBisTaktAus")]];
		meta.endExclusion = boundaries[initFromSource[this.get("buchzeitBisTaktA")]]; //todo validate
		meta.maxDuration = checkMaxDuration ? boundaries["maxDuration"] : false;

		// ["buchzeitVonInit", "buchzeitBisInit", "buchzspInit"]
		if (!this.get("buchzeitVonInitAus").trim()) meta.bookingTime = this.get("buchzeitVonInit");
		if (!this.get("buchzeitBisInitAus").trim()) meta.bookingTimeEnd = this.get('buchzeitBisInit');
		if (!meta.startStepSize || meta.startStepSize < 100) meta.startStepSize = this.get('buchzeitVonTakt');
		if (!meta.endStepSize || meta.endStepSize < 100) meta.endStepSize = this.get('buchzeitBisTakt');
		if (!meta.startStepSize || meta.startStepSize < 100) meta.startStepSize = 100;
		if (!meta.endStepSize || meta.endStepSize < 100) meta.endStepSize = meta.startStepSize;
		return meta
	},
});

let EntryDescriptionCollection = Backbone.Collection.extend({
	url: "../json/entryDescription",
	model: EntryDescriptionModel,
	/**
	 * @param model {BSModuleBB.BookingModel | BSModuleBB.PersurlModel | {bookingType, hourCategory}}
	 * @return {EntryDescriptionModel}
	 */
	getFor: function (model) {
		let searchObject = {
			bookingType: " ",
			hourCategory: " ",
		};
		if (model instanceof Backbone.Model) {
			//extract info from model
			switch (typeof model.getSisName === 'function' && model.getSisName()) {
				case SisName.Booking:
					searchObject.bookingType = model.get('bookingType');
					searchObject.hourCategory = model.get('hourCategory');
					break;
				case SisName.Persurl:
					searchObject.hourCategory = model.get('hourCategory');
					break;
				default:
					return null;
			}
		} else if (_.isPlainObject(model)) {
			if (model.bookingType && model.bookingType.trim()) searchObject.bookingType = model.bookingType;
			if (model.hourCategory && model.hourCategory.trim()) searchObject.hourCategory = model.hourCategory;
		} else {
			return null;
		}
		// find in descriptions;
		let eDesc = this.findWhere(searchObject);
		if (!eDesc) {
			if (this.length) console.error("Could not resolve Model! Check application Parameters: " + JSON.stringify(searchObject));
			return null;
		}
		return eDesc; //only first found
	},
	/**
	 * @param {number} entryDescriptionId
	 * @param selfadmin
	 * @param [kost]
	 * @return {BookingModel | PersurlModel} child instance witch set bookingType+hourCategory
	 */
	getNewModelInstance: function (entryDescriptionId, selfadmin, kost) {
		let desc = this.get(entryDescriptionId);
		if (!TypePerm.check(desc, Perm.crt, selfadmin)) {
			console.error('No write permission for ', desc);
			return null;
		}

		let bookingType = desc.get("bookingType").trim();
		let hourCategory = desc.get("hourCategory").trim();

		let newModel;
		if (bookingType) {
			newModel = new BSModuleBB.BookingModel();
			if (kost) newModel.set('bewEinh', kost)
		} else if (!bookingType && hourCategory) {
			newModel = new BSModuleBB.PersurlModel();
		} else return null;

		if (bookingType) newModel.set('bookingType', bookingType, {silent: true});
		if (hourCategory) newModel.set('hourCategory', hourCategory, {silent: true});

		return newModel;
	},
	getInitFixedKostkeys: function () {
		return initEntryDescriptionMaps?.flatMap(x => x.kost?.trim() ? [x.kost] : [])
	},
	getInitModelInstances: function (selfadmin, kostkeys) {
		let models = []
		_.each(initEntryDescriptionMaps, ({entryDescId, kost, onlyOne}) => {
			if (onlyOne) {
				models.push(this.getNewModelInstance(entryDescId, selfadmin, kost))
			} else {
				kostkeys.forEach(k => {
					models.push(this.getNewModelInstance(entryDescId, selfadmin, k))
				})
			}
		})
		return _.compact(models)
	},
});

export const entryDescCollection = new EntryDescriptionCollection();
window.sis = {
	...window.sis,
	entryDescCollection: entryDescCollection,
};

let fetched = false;

export function fetchOnceEntryDescriptions() {
	if (!fetched) {
		fetched = true;
		const init = Promise.all([
			Paramdiv.fetchMultiplePromise(
				Paramdiv.ID.approvalBookingTypes,
				Paramdiv.ID.approvalCostCenterBookingTypes,
				Paramdiv.ID.approvalNoCheck,
				Paramdiv.ID.bookingEndNoCheck,
			),
			entryDescCollection.fetch(),
			fetchAppDeviceStrings(),
		]).then(([
			[
				freigabeBuartList,
				kostFreigabeBuartList,
				approvalNoCheckList,
				bookingEndNoCheckList,
			],
			_entryDescriptions,
			_appDeviceStrings,
		]) => {
			entryDescCollection.each(m => {
				let {bookingType, hourCategory} = m.attributes;
				bookingType = bookingType.trim();
				hourCategory = hourCategory.trim();
				let approvalPze = false;
				let approvalPzeKost = false;
				let approvalNoCheck = false;
				let bookingEndNoCheck = false;
				if (!hourCategory) {
					if (_.includes(freigabeBuartList.join(',').split(/[,;]/), bookingType)) approvalPze = true;
					if (_.includes(kostFreigabeBuartList.join(',').split(/[,;]/), bookingType)) approvalPzeKost = true;
					if (approvalPze || approvalPzeKost || _.includes(approvalNoCheckList, bookingType)) approvalNoCheck = true;
					if (_.includes(bookingEndNoCheckList, bookingType)) bookingEndNoCheck = true;
				}
				m.set({approvalPze, approvalPzeKost, approvalNoCheck, bookingEndNoCheck});
			});
			return entryDescCollection.toJSON();
		});
		init.then($_defer.resolve);
		init.catch($_defer.reject);
		init.catch(generateErrorHandler("Fehler beim initialisieren der Buchungs- und Stundenarten"));
	}
	return $_defer;
}

let initIdsFetched = false;

export function fetchOnceInitIds() {
	if (!initIdsFetched) {
		initIdsFetched = true;
		$.ajax({
			url: "../json/entryDescription/init",
			dataType: "json",
		}).done(function (data) {
			initEntryDescriptionMaps = data;
		}).done($_deferInitIds.resolve).fail($_deferInitIds.reject);
	}
	return $_deferInitIds;
}

export default {
	collection: entryDescCollection,
	fetchOnceDefer: $_defer,
	fetchOnce: fetchOnceEntryDescriptions,
	fetchOnceInitEntryDescritptionIds: fetchOnceInitIds,
};